import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Film, RefreshCw, Download, Eye, ExternalLink, Wand2 } from "lucide-react";
import { devWarn } from "@/lib/devlog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * 2-minute public marketing reel — typography-forward, no portal screens,
 * stylized UI fragments only. Auto-publishes to the public /demo page
 * the moment a render completes.
 */
export default function MarketingVideoPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/marketing-video/status");
      setStatus(r.data);
      setPolling(r.data?.status === "running");
    } catch (e) {
      devWarn("marketing-video/status failed", e);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (!polling) return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [polling]);

  const trigger = async () => {
    setBusy(true);
    try {
      await api.post("/admin/marketing-video/render");
      toast.success("Marketing video render queued — about 6 minutes.");
      setPolling(true);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const dlUrl = status?.exists ? `${BACKEND_URL}${status.url}` : null;
  const isRunning = status?.status === "running";
  const progress = Number(status?.progress || 0);

  return (
    <section
      className="mt-10 rounded-2xl border border-slate-200 bg-white p-6"
      data-testid="marketing-video-panel"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700">
            <Film className="w-3.5 h-3.5" /> Public marketing reel · 2 min
          </div>
          <h3 className="mt-1 font-display text-2xl font-bold text-slate-900 tracking-tight">
            Public-facing pitch video. Zero portal screens.
          </h3>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl leading-relaxed">
            6 typography-forward scenes · OpenAI TTS narration ("sage" voice) ·
            ambient pad · 1080p H.264 / AAC · stylized UI fragments only — no
            full portal pages so competitive advantage stays intact. Renders to
            <span className="font-mono text-[12px] mx-1">/api/static/demo-videos/marketing-2min-latest.mp4</span>
            and auto-embeds on <span className="font-bold">/demo</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={isRunning}
            data-testid="marketing-video-refresh"
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-60"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRunning ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            type="button"
            onClick={trigger}
            disabled={busy || isRunning}
            data-testid="marketing-video-render"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-5 py-2 text-xs font-bold hover:bg-slate-800 disabled:opacity-60"
          >
            <Film className="w-3.5 h-3.5" />
            {isRunning ? `Rendering · ${progress}%` : busy ? "Queueing…" : status?.exists ? "Re-render" : "Render reel"}
          </button>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3 flex-wrap text-xs">
        {isRunning ? (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Rendering · {progress}%
            </span>
            <div className="flex-1 max-w-md h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-amber-400 transition-all" style={{ width: `${progress}%` }} />
            </div>
          </>
        ) : status?.exists ? (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-1 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Live · {Math.round((status.size_bytes || 0) / 1024 / 1024)} MB
              {status.duration_sec ? ` · ${Math.round(status.duration_sec)}s` : ""}
              {status.scene_count ? ` · ${status.scene_count} scenes` : ""}
            </span>
            <span className="text-slate-500">
              Last rendered{" "}
              {status.finished_at
                ? new Date(status.finished_at).toLocaleString(undefined, {
                    year: "numeric", month: "short", day: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })
                : "—"}
            </span>
          </>
        ) : (
          <span className="text-slate-500">No render yet — click <b>Render reel</b> to start.</span>
        )}
      </div>

      {status?.exists && dlUrl && (
        <div className="mt-5 grid lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7">
            <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 bg-slate-950"
                 style={{ aspectRatio: "16 / 9" }}>
              <video
                key={dlUrl}
                src={dlUrl}
                className="w-full h-full"
                controls
                preload="metadata"
                data-testid="marketing-video-preview"
              />
            </div>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-2 text-sm">
            <a
              href={dlUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="marketing-video-open-new-tab"
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-blue-600 text-white px-4 py-2.5 text-xs font-bold hover:bg-blue-700"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open MP4 in new tab
            </a>
            <a
              href={dlUrl}
              download
              data-testid="marketing-video-download"
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-2.5 text-xs font-bold hover:bg-slate-800"
            >
              <Download className="w-3.5 h-3.5" /> Download MP4
            </a>
            <a
              href="/demo"
              target="_blank"
              rel="noreferrer"
              data-testid="marketing-video-view-live"
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2.5 text-xs font-semibold"
            >
              <Eye className="w-3.5 h-3.5" /> View on /demo
            </a>
            <div className="text-[11px] text-slate-500 leading-snug mt-2 inline-flex items-start gap-1.5">
              <Wand2 className="w-3 h-3 mt-0.5 text-slate-400 shrink-0" />
              <span>
                <b>Auto-render is on.</b> Edit <span className="font-mono">marketing_video_scenes.py</span> and the latest cut ships within ~30 seconds — no clicks required. The reel also auto-publishes to <span className="font-mono">/demo</span> after every successful render.
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
